// SCR-STAFF-201 تب «اقلام مصرفی (BOM)» (AP-INV-02؛ US-CAT-014، BR-CAT-014): کالای INV فعال، مقدار به ازای یک واحد خدمت به واحد مصرف کالا
// (C-AGENT-1201)، قابل تغییر در جلسه و الزامی. ردیف کالای غیرفعال‌شده برای بازبینی علامت می‌خورد. فقط با feature.inv.inventory نمایش داده می‌شود.
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type ServiceBomView, type ServiceView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, LoadingState } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { WriteNotice } from '../settings/common';
import { fromMilli, toMilli } from '../inventory/stockCommon';
import { SavedNotice } from './common';

interface BomRow {
  itemId: string;
  quantity: string;
  isAdjustable: boolean;
  isMandatory: boolean;
  needsReview: boolean;
}

const bomKey = (tenantId: string | undefined, serviceId: string) => ['catalog', 'service-bom', tenantId, serviceId] as const;

function toRows(view: ServiceBomView): BomRow[] {
  return view.items.map((item) => ({ itemId: item.itemId, quantity: String(fromMilli(item.quantityMilli)), isAdjustable: item.isAdjustable, isMandatory: item.isMandatory, needsReview: item.needsReview || !item.isItemActive }));
}

export function BomTab({ service }: { service: ServiceView }) {
  const { tenantApi, session } = useTenantSession();
  const bom = useQuery({
    queryKey: bomKey(session?.tenantId, service.id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/services/{id}/bom', { params: { path: { id: service.id } } })),
  });
  if (bom.isPending) {
    return <LoadingState rows={3} />;
  }
  if (bom.error) {
    return <ErrorState error={bom.error} onRetry={() => void bom.refetch()} />;
  }
  return <BomEditor service={service} view={bom.data} />;
}

function BomEditor({ service, view }: { service: ServiceView; view: ServiceBomView }) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.service.manage');
  const [rows, setRows] = useState<BomRow[]>(() => toRows(view));
  const [saved, setSaved] = useState(false);
  useEffect(() => setRows(toRows(view)), [view]);
  const items = useQuery({
    queryKey: ['inventory', 'items', session?.tenantId, 'options'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/items', { params: { query: { active: true, limit: 100 } } })),
  });
  const update = (index: number, patch: Partial<BomRow>) => setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const invalid = rows.some((row) => !row.itemId || (toMilli(row.quantity) ?? 0) <= 0);

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/catalog/services/{id}/bom', {
          params: { path: { id: service.id } },
          body: { items: rows.map((row) => ({ itemId: row.itemId, quantityMilli: toMilli(row.quantity) ?? 0, isAdjustable: row.isAdjustable, isMandatory: row.isMandatory })) },
        }),
      ),
    onMutate: () => setSaved(false),
    onSuccess: (updated) => {
      queryClient.setQueryData(bomKey(session?.tenantId, service.id), updated);
      setSaved(true);
    },
  });

  const unitOf = (itemId: string) => {
    const item = items.data?.items.find((candidate) => candidate.id === itemId);
    return item ? t(`inv:unit.${item.consumptionUnit}`) : '';
  };
  const known = (itemId: string) => items.data?.items.some((candidate) => candidate.id === itemId) ?? true;

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="service-bom">
      <Stack spacing={2}>
        <WriteNotice write={write} />
        <Typography variant="body2" color="text.secondary">
          {t('cat:bom.help')}
        </Typography>
        {view.needsReview && (
          <Alert severity="warning" data-testid="bom-needs-review">
            {t('cat:bom.needs_review')}
          </Alert>
        )}
        {rows.map((row, index) => (
          <Stack key={index} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
            <TextField
              select
              size="small"
              required
              label={t('cat:bom.item')}
              value={known(row.itemId) ? row.itemId : ''}
              onChange={(event) => update(index, { itemId: event.target.value, needsReview: false })}
              error={row.needsReview}
              helperText={row.needsReview ? t('cat:bom.item_inactive') : undefined}
              sx={{ minWidth: 260 }}
              disabled={!write.canWrite}
              slotProps={{ htmlInput: { 'data-testid': `bom-item-${index}` } }}
            >
              {(items.data?.items ?? []).map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              required
              label={t('cat:bom.quantity')}
              value={row.quantity}
              onChange={(event) => update(index, { quantity: event.target.value })}
              error={Boolean(row.quantity) && (toMilli(row.quantity) ?? 0) <= 0}
              disabled={!write.canWrite}
              sx={{ width: 150 }}
              slotProps={{ htmlInput: { inputMode: 'decimal', dir: 'ltr', 'data-testid': `bom-quantity-${index}` }, input: { endAdornment: <Typography variant="caption">{unitOf(row.itemId)}</Typography> } }}
            />
            <FormControlLabel control={<Checkbox checked={row.isAdjustable} disabled={!write.canWrite} onChange={(event) => update(index, { isAdjustable: event.target.checked })} />} label={t('cat:bom.adjustable')} />
            <FormControlLabel control={<Checkbox checked={row.isMandatory} disabled={!write.canWrite} onChange={(event) => update(index, { isMandatory: event.target.checked })} />} label={t('cat:bom.mandatory')} />
            <IconButton aria-label={t('cat:bom.remove')} disabled={!write.canWrite} onClick={() => setRows((current) => current.filter((_row, i) => i !== index))}>
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        ))}
        {rows.length === 0 && <Typography color="text.secondary">{t('cat:bom.empty')}</Typography>}
        <Stack direction="row" spacing={1}>
          <Button startIcon={<AddIcon />} disabled={!write.canWrite || rows.length >= 50} onClick={() => setRows((current) => [...current, { itemId: '', quantity: '1', isAdjustable: true, isMandatory: false, needsReview: false }])} data-testid="bom-add">
            {t('cat:bom.add')}
          </Button>
          <Button variant="contained" disabled={!write.canWrite || invalid || save.isPending} onClick={() => save.mutate()} data-testid="bom-save">
            {t('common:actions.save')}
          </Button>
        </Stack>
        {save.error && <ErrorState error={save.error} />}
        <SavedNotice show={saved} />
      </Stack>
    </Paper>
  );
}
